import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { CalendarIcon, UnmutedCameraIcon } from '@streamr/tv-ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import styled from 'styled-components';
import { Button } from '../components/Button';
import { BroadcastFrame, BroadcastFrameList, } from '../components/frames/BroadcastFrame';
import { LobbyFrame } from '../components/frames/LobbyFrame';
import { CenteredContentContainer } from '../components/layout/content/CenteredContentContainer';
import { FooterWithLeftLogo } from '../components/layout/footers/FooterWithLeftLogo';
import { HeaderWithLeftLogoAndControls } from '../components/layout/headers/HeaderWithLeftLogoAndControls';
import { PageLayout } from '../components/layout/PageLayout';
import { scheduleBroadcast } from '../components/popups/ScheduleBroadcastPopup';
import { showSettingsPopup } from '../components/popups/SettingsPopup';
import { WorkspaceControls } from '../components/shared/WorkspaceControls';
import { BroadcastStateService } from '../services/broadcaststate/BroadcastStateService';
import { ScheduleService } from '../services/schedule/ScheduleService';
import { TimeService } from '../services/time/TimeService';
import { trackPageView } from '../utils/matomo';
import { formBroadcastPath, formBroadcastUrl, parseBroadcastFromUrl, } from '../utils/url';
// Track when the script is loaded in production
// this emulates the behavior of embedding the tracking
// script in the HTML.
trackPageView();
function useThrottledTime(intervalInMillis = 5000) {
    const now = TimeService.getOutput().useTime();
    const [throttled, setThrottled] = useState(() => now ?? Date.now());
    useEffect(() => {
        if (now !== undefined && now - throttled >= intervalInMillis) {
            setThrottled(now);
        }
    }, [now, throttled, intervalInMillis]);
    return throttled;
}
const HeaderControls = ({ broadcastAmount, onActivateBroadcastClick, }) => {
    return (_jsxs(_Fragment, { children: [broadcastAmount > 0 && (_jsxs(HeaderButtons, { children: [_jsx(PillHeaderButton, { type: "button", kind: "secondary", size: "mini", shape: "pill", icon: _jsx(CalendarIcon, {}), onClick: () => {
                            scheduleBroadcast();
                        } }), _jsx(RegularHeaderButton, { type: "button", kind: "secondary", size: "mini", icon: _jsx(CalendarIcon, {}), onClick: () => {
                            scheduleBroadcast();
                        }, children: "Schedule" }), _jsx(PillHeaderButton, { type: "button", size: "mini", shape: "pill", icon: _jsx(UnmutedCameraIcon, { fill: "#f65f0a" }), onClick: () => {
                            onActivateBroadcastClick(undefined, true);
                        } }), _jsx(RegularHeaderButton, { type: "button", size: "mini", icon: _jsx(UnmutedCameraIcon, { fill: "#f65f0a" }), onClick: () => {
                            onActivateBroadcastClick(undefined, true);
                        }, children: "Broadcast" })] })), _jsx(WorkspaceControls, { onKnobsClick: (anchorRef) => {
                    showSettingsPopup({
                        anchorRef,
                    });
                } })] }));
};
export function HomePage() {
    const broadcasts = ScheduleService.getOutput().useScheduledBroadcasts();
    const time = useThrottledTime(30000);
    const [pastBroadcasts, upcomingBroadcasts] = useMemo(() => {
        const past = [];
        const upcoming = [];
        for (const broadcast of broadcasts) {
            (broadcast.startsAt < time ? past : upcoming).push(broadcast);
        }
        past.reverse();
        return [past, upcoming];
    }, [broadcasts, time]);
    const navigate = useNavigate();
    const activateBroadcast = useCallback((savedBroadcast, navigateToBroadcastPage) => {
        BroadcastStateService.getNotificationInterface().notifyActivateBroadcast(savedBroadcast, navigateToBroadcastPage ? navigate : undefined);
    }, [navigate]);
    useEffect(() => {
        // Support legacy broadcast URLs TODO remove within few months
        const broadcast = parseBroadcastFromUrl(new URL(window.location.href));
        if (broadcast !== undefined) {
            // We are now at home page (i.e. at route /), but URL parser
            // says that we should be on a broadcast page. The reason must be
            // that we are using legacy hash-router URLS. Therefore we
            // form a URL with the current broadcast URL format and redirect
            // the user there.
            console.log('Redirect legacy URL');
            window.location.href = formBroadcastUrl(broadcast);
        }
    }, []);
    return (_jsx(_Fragment, { children: _jsx(PageLayout, { header: _jsx(HeaderWithLeftLogoAndControls, { controls: _jsx(HeaderControls, { broadcastAmount: broadcasts.length, onActivateBroadcastClick: activateBroadcast }) }), footer: _jsx(FooterWithLeftLogo, {}), children: _jsxs(CenteredContentContainer, { children: [broadcasts.length === 0 && _jsx(LobbyFrame, {}), upcomingBroadcasts.length > 0 && (_jsx(BroadcastFrameList, { multi: broadcasts.length > 1, title: "Upcoming", children: upcomingBroadcasts.map((broadcast) => (_jsx(BroadcastFrame, { broadcast: broadcast, onActivateBroadcastClick: () => {
                                activateBroadcast(broadcast, true);
                            } }, broadcast.id))) })), pastBroadcasts.length > 0 && (_jsx(BroadcastFrameList, { multi: broadcasts.length > 1, title: upcomingBroadcasts.length > 0
                            ? 'Past'
                            : undefined, children: pastBroadcasts.map((broadcast) => (_jsx(BroadcastFrame, { broadcast: broadcast, onActivateBroadcastClick: () => {
                                activateBroadcast(broadcast, true);
                            }, onRecordingsClick: () => {
                                activateBroadcast(broadcast, false);
                                navigate(formBroadcastPath(broadcast, '/recordings'));
                            } }, broadcast.id))) }))] }) }) }));
}
const PillHeaderButton = styled(Button) `
    padding: 0;
    width: 2.75rem;
    min-width: 2.75rem;
`;
const RegularHeaderButton = styled(Button) ``;
const HeaderButtons = styled.div `
    align-items: center;
    display: grid;
    gap: 1rem;
    grid-template-columns: auto auto;
    justify-items: end;

    button {
        height: 2.75rem;
    }

    ${RegularHeaderButton} {
        display: none;
    }

    @media (min-width: 37.5rem) {
        grid-template-columns: auto auto;

        ${PillHeaderButton} {
            display: none;
        }

        ${RegularHeaderButton} {
            display: flex;
        }
    }
`;
